// src/pages/MasterDataPage.js
import React, { useState, useEffect, useCallback } from 'react';
import MasterDataModal from '../components/MasterDataModal';
import MasterDataAPITab from '../components/MasterDataAPITab';
import IssuerTab from '../components/IssuerTab';
import MerchantPGATab from '../components/MerchantPGATab';
import TxnDetailMethodTab from '../components/TxnDetailMethodTab';
import './MasterDataPage.css';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const API_URL = '/api/master-data';

function MasterDataPage() {
  const { authFetch } = useAuth();
  const [activeTab, setActiveTab] = useState('config'); // config | api

  // States untuk Master Data Config (Scraping)
  const [masterData, setMasterData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // State untuk modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});

  const fetchMasterData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authFetch(API_URL);
      const data = await response.json();
      setMasterData(data);
    } catch (error) {
      console.error("Gagal mengambil master data:", error);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    if (activeTab === 'config') {
      fetchMasterData();
    }
  }, [activeTab, fetchMasterData]);

  const handleOpenAddModal = () => {
    setIsEditing(false);
    setCurrentRecord({ is_active: true });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (record) => {
    setIsEditing(true);
    setCurrentRecord(record);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentRecord({});
  };

  const handleSave = async () => {
    const url = isEditing ? `${API_URL}/${currentRecord.id}` : API_URL;
    const method = isEditing ? 'PUT' : 'POST';

    try {
      const response = await authFetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(currentRecord),
      });
      if (!response.ok) throw new Error('Gagal menyimpan data');
      toast.success(`Record berhasil ${isEditing ? 'diperbarui' : 'ditambahkan'}!`);
      handleCloseModal();
      fetchMasterData();
    } catch (error) {
      console.error(error);
      toast.error('Gagal menyimpan record.');
    }
  };

  const handleDelete = async (recordId) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus record ini?')) {
      try {
        const response = await authFetch(`${API_URL}/${recordId}`, { method: 'DELETE' });
        if (!response.ok) throw new Error('Gagal menghapus data');
        toast.success('Record berhasil dihapus!');
        fetchMasterData();
      } catch (error) {
        console.error(error);
        toast.error(`Gagal menghapus data: ${error.message}`);
      }
    }
  };

  const handleToggleStatus = async (record, newStatus) => {
    try {
      setMasterData(prevData =>
        prevData.map(item =>
          item.id === record.id ? { ...item, is_active: newStatus } : item
        )
      );

      const response = await authFetch(`${API_URL}/${record.id}/toggle`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Gagal memperbarui status');
      }
    } catch (error) {
      console.error(error);
      setMasterData(prevData =>
        prevData.map(item =>
          item.id === record.id ? { ...item, is_active: !newStatus } : item
        )
      );
      toast.error('Gagal memperbarui status. Silakan coba lagi.');
    }
  };

  return (
    <div className="master-data-container">
      <div className="dashboard-header">
        <h1>Master Data Management</h1>
      </div>

      <div className="tab-navigation">
        <button 
          className={activeTab === 'config' ? 'tab-btn active' : 'tab-btn'} 
          onClick={() => setActiveTab('config')}
        >
          Master Data Config
        </button>
        <button 
          className={activeTab === 'api' ? 'tab-btn active' : 'tab-btn'} 
          onClick={() => setActiveTab('api')}
        >
          Master Data API
        </button>
        <button 
          className={activeTab === 'issuer' ? 'tab-btn active' : 'tab-btn'} 
          onClick={() => setActiveTab('issuer')}
        >
          Master Data Issuer
        </button>
        <button 
          className={activeTab === 'merchant-pga' ? 'tab-btn active' : 'tab-btn'} 
          onClick={() => setActiveTab('merchant-pga')}
        >
          Merchant PGA
        </button>
        <button 
          className={activeTab === 'txn-detail-method' ? 'tab-btn active' : 'tab-btn'} 
          onClick={() => setActiveTab('txn-detail-method')}
        >
          Txn Detail Methods
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'config' && (
          <div>
            <div className="tab-header-actions">
              <h2>Scraping Configurations</h2>
              <button onClick={handleOpenAddModal} className="add-new-btn">Add New</button>
            </div>

            {loading ? (
              <p>Loading data...</p>
            ) : (
              <div className="table-responsive">
                <table className="master-data-table">
                  <thead>
                    <tr>
                      <th>PGA Name</th>
                      <th>Vendor</th>
                      <th>Project</th>
                      <th>Agent</th>
                      <th>Platform</th>
                      <th>Provider ID</th>
                      <th>Merchant ID</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {masterData.map((item) => (
                      <tr key={item.id}>
                        <td>{item.pga_name}</td>
                        <td>{item.vendor}</td>
                        <td>{item.project}</td>
                        <td>{item.agent}</td>
                        <td>{item.platform}</td>
                        <td>{item.provider_id}</td>
                        <td>{item.merchant_id}</td>
                        <td>
                          <label className="switch-table">
                            <input 
                              type="checkbox" 
                              checked={item.is_active} 
                              onChange={(e) => handleToggleStatus(item, e.target.checked)}
                            />
                            <span className="slider round"></span>
                          </label>
                        </td>
                        <td className="actions-cell">
                          <button onClick={() => handleOpenEditModal(item)}>Edit</button>
                          <button onClick={() => handleDelete(item.id)} className="delete-btn">Delete</button>
                        </td>
                      </tr>
                    ))}
                    {masterData.length === 0 && (
                      <tr>
                        <td colSpan="9" style={{textAlign: 'center', padding: '20px'}}>No records found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <MasterDataModal
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              onSave={handleSave}
              record={currentRecord}
              setRecord={setCurrentRecord}
              isEditing={isEditing}
              mode="scraping"
            />
          </div>
        )}

        {activeTab === 'api' && (
          <MasterDataAPITab />
        )}

        {activeTab === 'issuer' && (
          <IssuerTab />
        )}

        {activeTab === 'merchant-pga' && (
          <MerchantPGATab />
        )}
        
        {activeTab === 'txn-detail-method' && (
          <TxnDetailMethodTab />
        )}
      </div>
    </div>
  );
}

export default MasterDataPage;